import merge from 'lodash/merge';
import {
  Box,
  Stack,
  Button,
  DialogActions,
  Typography,
  IconButton,
  Autocomplete,
  Chip,
  TextField,
} from '@mui/material';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import FormProvider from '../../../components/hook-form/FormProvider';
import {
  RHFCheckbox,
  RHFSelect,
  RHFTextField,
} from '../../../components/hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import { IPrescreeningSchema } from '../../../@types/specsHistory';
import { ChangeEvent, useEffect, useState } from 'react';
import { Tags } from '../../../@types/tags.type';
import { TagsService } from '../../../api/tags.service';
import ConfirmDialog from '../../../components/confirm-dialog/ConfirmDialog';
import { enqueueSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../utils/util';

type Props = {
  onCancel: VoidFunction;
  draftPrescreening: IPrescreeningSchema;
  onSubmitForm: any;
  setAverages: (type: string, value: number) => void;
  averages: {
    IOPs: number;
    CCTs: number;
  };
  selectedChips: {
    reasonForVisit: string[];
    hobbies: string[];
  };
  setSelectedChips: React.Dispatch<
    React.SetStateAction<{
      reasonForVisit: string[];
      hobbies: string[];
    }>
  >;
  setDataPrescreening: (data: any) => void;
};

type FormValuesProps = IPrescreeningSchema;

const getInitialValues = (draftPrescreening: IPrescreeningSchema) => {
  const initialEvent: FormValuesProps = {
    reasonForVisit: '',
    hobbies: '',
    isDriver: false,
    isVduUse: false,
    isSmoker: false,
    autoRefractor: {
      rightSphericalPower: '',
      leftSphericalPower: '',
      rightCylinder: '',
      leftCylinder: '',
      rightAxis: '',
      leftAxis: '',
      rightDistancePD: '',
      leftDistancePD: '',
      rightBVD: '',
      leftBVD: '',
    },
    eyePressures: [],
    specificationHistories: [
      {
        condition: 'Average',
        notes: '',
        specifications: '',
        rightSphericalPower: '',
        leftSphericalPower: '',
        rightCylinder: '',
        leftCylinder: '',
        rightAxis: '',
        leftAxis: '',
        rightDistancePD: '',
        leftDistancePD: '',
        rightBVD: '',
        leftBVD: '',
      },
    ],
  };

  if (draftPrescreening) {
    return merge({}, initialEvent, draftPrescreening);
  }

  return initialEvent;
};

/** Tags match on trimmed, case-insensitive text ("driving" === "Driving"). */
const isSameTag = (a: string | undefined, b: string | undefined): boolean =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

const IOP_MIN = 1;
const IOP_MAX = 80;

const CCT_MIN = 250;
const CCT_MAX = 700;

const iopErrorKey = (rowId: string) => `IOPs-${rowId}`;
const cctErrorKey = (rowId: string) => `CCT-${rowId}`;

const AddSpecificationHistoryButton = ({ fields, append }: any) => {
  const handleAddSpecificationHistory = () => {
    append({
      condition: 'Average',
      notes: '',
      specifications: '',
    });
  };

  return (
    <Button
      variant="outlined"
      color="primary"
      onClick={handleAddSpecificationHistory}
    >
      Add Specification History
    </Button>
  );
};

const AddEyePressuresButton = ({ fields, append }: any) => {
  const handleEyePressures = () => {
    append({
      IOPs: '' as unknown as number,
      CCT: '' as unknown as number,
      instrument: '',
    });
  };

  return (
    <Button variant="outlined" color="primary" onClick={handleEyePressures}>
      Add Eye Pressures
    </Button>
  );
};
const AddNewPrescreening = ({
  onCancel,
  draftPrescreening,
  onSubmitForm,
  setAverages,
  averages,
  selectedChips,
  setSelectedChips,
  setDataPrescreening,
}: Props) => {
  const [errorMessages, setErrorMessages] = useState<{
    [key: string]: string | null;
  }>({});

  const [availableChips, setAvailableChips] = useState<Tags[]>([]);
  const [currentFreeSoloValue, setCurrentFreeSoloValue] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [inputValues, setInputValues] = useState<{
    reasonForVisit: '';
    hobbies: '';
  }>({
    reasonForVisit: '',
    hobbies: '',
  });
  const [currentFieldName, setCurrentFieldName] = useState<
    keyof typeof inputValues | null
  >(null);

  const getAllChips = async () => {
    try {
      const category = 'Prescreening';
      const tagsService = new TagsService();
      const res = await tagsService.getAllTags(category);
      setAvailableChips(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getAllChips();
  }, []);

  const handleInputChange = (e: any, fieldName: string) => {
    const value = Number(e.target.value);

    // Define validation for the value
    if (value < 1) {
      setErrorMessages((prev) => ({
        ...prev,
        [fieldName]: 'Values less than 1 are not allowed',
      }));
      e.target.value = 1;
    } else if (value > 180) {
      setErrorMessages((prev) => ({
        ...prev,
        [fieldName]: 'Values above 180 are not allowed',
      }));
      e.target.value = 180;
    } else {
      setErrorMessages((prev) => ({ ...prev, [fieldName]: null })); // Clear message if valid
    }

    if (e.target.value.length > 3) {
      e.target.value = e.target.value.slice(0, 3); // Enforce a max of 3 digits
    }
  };

  const normalizeClinicalNumber = (
    rawValue: string,
    {
      min,
      max,
      step,
      decimals = 2,
      forceNegative = false,
      integer = false,
    }: {
      min: number;
      max: number;
      step?: number;
      decimals?: number;
      forceNegative?: boolean;
      integer?: boolean;
    },
  ) => {
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      return '';
    }

    let value = Number(rawValue);
    if (Number.isNaN(value)) return '';

    if (forceNegative && value > 0) value = -value;

    value = Math.max(min, Math.min(max, value));

    if (integer) {
      value = Math.round(value);
      return String(value);
    }

    if (step && step > 0) {
      value = Math.round(value / step) * step;
    }

    return value.toFixed(decimals);
  };

  const validateIOPsValue = (raw: string, rowId: string) => {
    const trimmed = (raw ?? '').trim();
    if (trimmed === '') {
      setErrorMessages((p) => ({ ...p, [iopErrorKey(rowId)]: null }));
      return true;
    }
    const n = Number(trimmed);
    if (Number.isNaN(n) || n < IOP_MIN || n > IOP_MAX) {
      setErrorMessages((p) => ({
        ...p,
        [iopErrorKey(rowId)]:
          `IOP must be between ${IOP_MIN} and ${IOP_MAX} mmHg`,
      }));
      return false;
    }
    setErrorMessages((p) => ({ ...p, [iopErrorKey(rowId)]: null }));
    return true;
  };

  const validateCCTValue = (raw: string, rowId: string) => {
    const trimmed = (raw ?? '').trim();
    if (trimmed === '') {
      setErrorMessages((p) => ({ ...p, [cctErrorKey(rowId)]: null }));
      return true;
    }
    const n = Number(trimmed);
    if (Number.isNaN(n) || n < CCT_MIN || n > CCT_MAX) {
      setErrorMessages((p) => ({
        ...p,
        [cctErrorKey(rowId)]:
          `CCT must be between ${CCT_MIN} and ${CCT_MAX} µm`,
      }));
      return false;
    }
    setErrorMessages((p) => ({ ...p, [cctErrorKey(rowId)]: null }));
    return true;
  };

  const methods = useForm({
    defaultValues: getInitialValues(draftPrescreening),
  });
  const {
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
    watch,
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    let eyePressureInvalid = false;
    const nextErr: Record<string, string | null> = {};
    (data.eyePressures ?? []).forEach((row: any, index: number) => {
      const rowId = String(eyePressuresFields[index]?.id ?? index);
      const iopRaw =
        row?.IOPs === '' || row?.IOPs === undefined || row?.IOPs === null
          ? ''
          : String(row.IOPs);
      const cctRaw =
        row?.CCT === '' || row?.CCT === undefined || row?.CCT === null
          ? ''
          : String(row.CCT);
      const iopEmpty = iopRaw.trim() === '';
      const cctEmpty = cctRaw.trim() === '';
      if (iopEmpty && cctEmpty) {
        return;
      }
      // IOP is the reading; CCT is optional (not every visit has pachymetry).
      if (iopEmpty) {
        nextErr[iopErrorKey(rowId)] = 'IOP is required for this reading';
        eyePressureInvalid = true;
      }
      if (!iopEmpty) {
        const n = Number(iopRaw);
        if (Number.isNaN(n) || n < IOP_MIN || n > IOP_MAX) {
          nextErr[iopErrorKey(rowId)] =
            `IOP must be between ${IOP_MIN} and ${IOP_MAX} mmHg`;
          eyePressureInvalid = true;
        }
      }
      if (!cctEmpty) {
        const n = Number(cctRaw);
        if (Number.isNaN(n) || n < CCT_MIN || n > CCT_MAX) {
          nextErr[cctErrorKey(rowId)] =
            `CCT must be between ${CCT_MIN} and ${CCT_MAX} µm`;
          eyePressureInvalid = true;
        }
      }
    });
    if (eyePressureInvalid) {
      setErrorMessages((prev) => ({ ...prev, ...nextErr }));
      enqueueSnackbar(
        'Please correct eye pressure values (IOP / CCT) before saving.',
        { variant: 'warning' },
      );
      return;
    }

    const isBlank = (value: unknown) => String(value ?? '').trim() === '';
    const draftPrescreening = {
      ...data,
      // An untouched "Add Eye Pressures" row is not a reading.
      eyePressures: (data.eyePressures ?? []).filter(
        (row: { IOPs?: unknown; CCT?: unknown }) =>
          !(isBlank(row?.IOPs) && isBlank(row?.CCT)),
      ),
      reasonForVisit: selectedChips?.reasonForVisit?.join(', '),
      hobbies: selectedChips?.hobbies?.join(', '),
    };
    onSubmitForm(draftPrescreening);

    localStorage.setItem(
      'draftPrescreening',
      JSON.stringify(draftPrescreening),
    );
    onCancel();
  };

  const {
    fields: specificationHistoryFields,
    append: appendSpecificationHistory,
    remove: removeSpecificationHistory,
  } = useFieldArray({
    control,
    name: 'specificationHistories',
  });

  const {
    fields: eyePressuresFields,
    append: appendEyePressure,
    remove: removeEyePressure,
  } = useFieldArray({
    control,
    name: 'eyePressures',
  });

  const watchedFields = useWatch({
    control,
    name: 'eyePressures',
  });

  const calculateAverages = () => {
    if (!watchedFields || watchedFields.length === 0) return;

    const totalIOP = watchedFields.reduce(
      (acc, curr) => acc + (parseFloat(String(curr?.IOPs)) || 0),
      0,
    );
    // CCT is optional: average only the readings that have one.
    const ccts = watchedFields
      .map((curr) => parseFloat(String(curr?.CCT)))
      .filter((n) => Number.isFinite(n));
    const totalCCT = ccts.reduce((acc, n) => acc + n, 0);

    setAverages('IOPs', totalIOP / watchedFields.length || 0);
    setAverages('CCTs', ccts.length ? totalCCT / ccts.length : 0);
  };

  useEffect(() => {
    calculateAverages();
  }, [watchedFields]);
  const toNumberOrNull = (raw: unknown): number | null => {
    const text = String(raw ?? '').trim();
    if (text === '') return null;
    const parsed = Number(text);
    return Number.isFinite(parsed) ? parsed : null;
  };

  /**
   * Optical transposition of ONE Lens Meter/Specs History row: per eye
   * sph += cyl, cyl = -cyl and the axis rotates by 90.
   *
   * Both eyes are read from the same row. The previous version read the left
   * eye from row index 1 while the right eye came from row 0, so on the usual
   * single-row form the left values were undefined/NaN and were written to a
   * row that does not exist; it also read the spherical powers as strings, so
   * "-2.00" + 1 produced the string "-2.001" instead of -1.
   */
  const handleTranspose = (index: number) => {
    let transposedAnyEye = false;

    (['right', 'left'] as const).forEach((side) => {
      const sphName =
        `specificationHistories.${index}.${side}SphericalPower` as const;
      const cylName =
        `specificationHistories.${index}.${side}Cylinder` as const;
      const axisName = `specificationHistories.${index}.${side}Axis` as const;

      // Without a cylinder there is nothing to transpose for this eye.
      const cyl = toNumberOrNull(methods.getValues(cylName));
      if (cyl === null) return;

      const sph = toNumberOrNull(methods.getValues(sphName)) ?? 0;
      const axis = toNumberOrNull(methods.getValues(axisName));

      methods.setValue(sphName, (sph + cyl).toFixed(2));
      methods.setValue(cylName, (-cyl).toFixed(2));
      if (axis !== null) {
        methods.setValue(
          axisName,
          String(axis + 90 < 181 ? axis + 90 : axis - 90),
        );
      }
      transposedAnyEye = true;
    });

    if (!transposedAnyEye) {
      enqueueSnackbar('Add a cylinder value to this row before transposing.', {
        variant: 'warning',
      });
    }
  };

  const [selectedSpecifications, setSelectedSpecifications] = useState({});

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const newValue = e.target.value;

    setSelectedSpecifications((prev) => ({
      ...prev,
      [index]: newValue,
    }));
  };

  const handleTagsInput = (
    event: React.ChangeEvent<{}>,
    value: string,
    fieldName: keyof typeof inputValues,
  ) => {
    setInputValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const appendPendingChip = (fieldName: keyof typeof inputValues) => {
    const pendingValue = (inputValues[fieldName] || '').trim();
    if (!pendingValue) return;

    setSelectedChips((prev) => {
      const existing = prev[fieldName] || [];
      const alreadyExists = existing.some(
        (item) => item.trim().toLowerCase() === pendingValue.toLowerCase(),
      );
      if (alreadyExists) {
        return prev;
      }

      const next = [...existing, pendingValue];
      updateDraftPrescreening(fieldName, next);
      return {
        ...prev,
        [fieldName]: next,
      };
    });

    setInputValues((prev) => ({ ...prev, [fieldName]: '' }));
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    fieldName: keyof typeof inputValues,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const typed = (inputValues[fieldName] ?? '').trim();
      if (!typed) return;
      // Both sides are trimmed and lower-cased, so typing "driving" is
      // recognised as the stored "Driving" and the "Add a New Tag" dialog
      // stays closed. A value already picked for this field counts as existing.
      const isExistingTag =
        availableChips.some((chip) => isSameTag(chip.tagName, typed)) ||
        (selectedChips?.[fieldName] ?? []).some((chip) =>
          isSameTag(chip, typed),
        );
      if (!isExistingTag) {
        setCurrentFreeSoloValue(typed);
        setCurrentFieldName(fieldName);
        setOpenModal(true);
      }
    }
  };

  const handleAddTag = async (
    value: string,
    fieldName: keyof typeof inputValues,
  ) => {
    const category = 'Prescreening';
    const tagName = value;
    const data = {
      tagName: tagName,
      category: category,
    };
    const tagsService = new TagsService();
    try {
      await tagsService.createTag(data);
      setInputValues((prev) => ({ ...prev, [fieldName]: '' }));
      getAllChips();
      enqueueSnackbar('New Tag Added Successfully', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(getApiErrorMessage(error), { variant: 'error' });
    }
    setOpenModal(false);
  };

  const updateDraftPrescreening = (fieldName: string, newValue: string[]) => {
    setDataPrescreening((prev: any) => ({
      ...prev,
      [fieldName]: newValue.join(', '),
    }));
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3} sx={{ px: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Autocomplete
            sx={{ width: '50%' }}
            multiple
            id="tags-filled"
            disableCloseOnSelect
            options={availableChips.map((chip) => chip.tagName)}
            freeSolo
            value={
              draftPrescreening
                ? draftPrescreening?.reasonForVisit
                    ?.split(',')
                    ?.map((s: any) => s.trim())
                    ?.filter(Boolean) || []
                : selectedChips?.reasonForVisit || []
            }
            onChange={(event, newValue) => {
              setSelectedChips((prev) => ({
                ...prev,
                reasonForVisit: newValue,
              }));
              updateDraftPrescreening('reasonForVisit', newValue);
            }}
            filterOptions={(options, state) => {
              return state.inputValue
                ? options.filter((item) =>
                    item.toLowerCase().includes(state.inputValue.toLowerCase()),
                  )
                : [];
            }}
            inputValue={inputValues?.reasonForVisit}
            onInputChange={(event, value) =>
              handleTagsInput(event, value, 'reasonForVisit')
            }
            renderTags={(value: readonly string[], getTagProps) =>
              value
                .filter((option) => option.trim() !== '')
                .map((option: string, index: number) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      variant="outlined"
                      label={option}
                      key={key}
                      {...tagProps}
                    />
                  );
                })
            }
            renderInput={(params) => (
              // A plain, single-line TextField: the chips are injected as the
              // input's startAdornment, so a fixed-height textarea
              // (multiline/rows) pushed the caret out of place, and RHF's
              // controlled value/ref fought the Autocomplete's own input.
              // Committed tags live in `selectedChips` and onSubmit overwrites
              // the payload from them, so this input is not registered with RHF.
              <TextField
                {...params}
                label="Reason For Visit"
                helperText="Type and press Enter, or click outside to add as a tag."
                onBlur={() => appendPendingChip('reasonForVisit')}
                onKeyDown={(event) =>
                  handleKeyDown(
                    event as React.KeyboardEvent<HTMLInputElement>,
                    'reasonForVisit',
                  )
                }
              />
            )}
          />
          <Autocomplete
            sx={{ width: '50%' }}
            disableCloseOnSelect
            options={availableChips.map((chip) => chip.tagName)}
            multiple
            id="tags-filled"
            freeSolo
            value={
              draftPrescreening
                ? draftPrescreening.hobbies
                    ?.split(',')
                    ?.map((s: any) => s.trim())
                    ?.filter(Boolean) || []
                : selectedChips?.hobbies || []
            }
            onChange={(event, newValue) => {
              setSelectedChips((prev) => ({
                ...prev,
                hobbies: newValue as string[],
              }));
              updateDraftPrescreening('hobbies', newValue);
            }}
            filterOptions={(options, state) => {
              return state.inputValue
                ? options.filter((item) =>
                    item.toLowerCase().includes(state.inputValue.toLowerCase()),
                  )
                : [];
            }}
            inputValue={inputValues.hobbies}
            onInputChange={(event, value) =>
              handleTagsInput(event, value, 'hobbies')
            }
            renderTags={(value: readonly string[], getTagProps) =>
              value
                .filter((option) => option.trim() !== '')
                .map((option: string, index: number) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      variant="outlined"
                      label={option}
                      key={key}
                      {...tagProps}
                    />
                  );
                })
            }
            renderInput={(params) => (
              // A plain, single-line TextField: the chips are injected as the
              // input's startAdornment, so a fixed-height textarea
              // (multiline/rows) pushed the caret out of place, and RHF's
              // controlled value/ref fought the Autocomplete's own input.
              // Committed tags live in `selectedChips` and onSubmit overwrites
              // the payload from them, so this input is not registered with RHF.
              <TextField
                {...params}
                label="Hobbies"
                helperText="Type and press Enter, or click outside to add as a tag."
                onBlur={() => appendPendingChip('hobbies')}
                onKeyDown={(event) =>
                  handleKeyDown(
                    event as React.KeyboardEvent<HTMLInputElement>,
                    'hobbies',
                  )
                }
              />
            )}
          />
        </Box>
        <Box sx={{ display: 'flex' }}>
          <RHFCheckbox sx={{ ml: 1, mt: 1 }} name="isDriver" label="Driver" />
          <RHFCheckbox sx={{ ml: 1, mt: 1 }} name="isSmoker" label="Smoker" />
          <RHFCheckbox sx={{ ml: 1, mt: 1 }} name="isVduUse" label="VDU Use" />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">Lens Meter/Specs History:</Typography>
            <Box>
              <AddSpecificationHistoryButton
                fields={specificationHistoryFields}
                append={appendSpecificationHistory}
              />
            </Box>
          </Box>
          {specificationHistoryFields.map((specification, index) => (
            <>
              <Box key={specification.id} mt={2} sx={{ display: 'flex' }}>
                <RHFSelect
                  native
                  name={`specificationHistories.${index}.condition`}
                  label="Condition"
                  InputLabelProps={{ shrink: true }}
                >
                  <option key="Average" value="Average">
                    Average
                  </option>
                  <option key="New" value="New">
                    New
                  </option>
                </RHFSelect>
                <RHFSelect
                  sx={{ ml: 2 }}
                  native
                  name={`specificationHistories.${index}.specifications`}
                  value={watch(
                    `specificationHistories.${index}.specifications`,
                  )}
                  label="Specifications"
                  onChange={(e) => {
                    handleChange(e, index);
                    setValue(
                      `specificationHistories.${index}.specifications`,
                      e.target.value,
                    );
                  }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="" disabled>
                    Please select
                  </option>

                  {['SVD', 'SVN', 'Bifocal', 'Varifocal'].map((option) => {
                    const isDisabled =
                      (option === 'SVD' || option === 'SVN') &&
                      Object.values(selectedSpecifications).includes(option);
                    return (
                      <option key={option} value={option} disabled={isDisabled}>
                        {option.charAt(0).toUpperCase() + option.slice(1)}{' '}
                        {/* Capitalize first letter */}
                      </option>
                    );
                  })}
                </RHFSelect>
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ ml: 2 }}
                  onClick={() => handleTranspose(index)}
                >
                  Transpose Values
                </Button>
                <IconButton onClick={() => removeSpecificationHistory(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                <RHFTextField
                  sx={{ mt: 2, mr: 3 }}
                  name={`specificationHistories.${index}.notes`}
                  label="Notes"
                  multiline
                  rows={2}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                ></Box>
                <Box
                  sx={{
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                    mt={1}
                  >
                    <Typography>Right:</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                      <Box
                        mr={1}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        Sph
                        <RHFTextField
                          sx={{ width: '150px' }}
                          name={`specificationHistories.${index}.rightSphericalPower`}
                          type="number"
                          inputProps={{
                            step: 0.25,
                            min: -20,
                            max: 20,
                          }}
                          onBlur={(e) =>
                            setValue(
                              `specificationHistories.${index}.rightSphericalPower`,
                              normalizeClinicalNumber(e.target.value, {
                                min: -20,
                                max: 20,
                                step: 0.25,
                              }),
                            )
                          }
                          InputProps={{
                            startAdornment:
                              Number(
                                watch(
                                  `specificationHistories.${index}.rightSphericalPower`,
                                ),
                              ) > 0
                                ? '+'
                                : null,
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          width: '1px',
                          margin: '0 10px',
                          height: '40px',
                          background: 'black',
                          position: 'relative',
                          top: '30px',
                          transform: 'rotate(14deg)',
                        }}
                      ></Box>
                      <Box
                        mr={1}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        Cyl
                        <RHFTextField
                          sx={{ width: '180px' }}
                          name={`specificationHistories.${index}.rightCylinder`}
                          type="number"
                          inputProps={{
                            step: 0.25,
                            min: -10,
                            max: 0,
                          }}
                          onBlur={(e) =>
                            setValue(
                              `specificationHistories.${index}.rightCylinder`,
                              normalizeClinicalNumber(e.target.value, {
                                min: -10,
                                max: 0,
                                step: 0.25,
                                forceNegative: true,
                              }),
                            )
                          }
                        />
                      </Box>
                      <Box
                        mr={1}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        Axis
                        <RHFTextField
                          sx={{ width: '180px' }}
                          name={`specificationHistories.${index}.rightAxis`}
                          type="number"
                          inputProps={{
                            step: 1,
                            min: 1,
                            max: 180,
                          }}
                          onBlur={(e) =>
                            setValue(
                              `specificationHistories.${index}.rightAxis`,
                              normalizeClinicalNumber(e.target.value, {
                                min: 1,
                                max: 180,
                                integer: true,
                              }),
                            )
                          }
                        />
                        {errorMessages['rightAxis'] && (
                          <Typography color="error" variant="caption">
                            {errorMessages['rightAxis']}
                          </Typography>
                        )}
                      </Box>
                      <Box
                        mr={1}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        Dist PD
                        <RHFTextField
                          sx={{ width: '180px' }}
                          name={`specificationHistories.${index}.rightDistancePD`}
                          type="number"
                          inputProps={{
                            step: 0.5,
                            min: 20,
                            max: 45,
                          }}
                          onBlur={(e) =>
                            setValue(
                              `specificationHistories.${index}.rightDistancePD`,
                              normalizeClinicalNumber(e.target.value, {
                                min: 20,
                                max: 45,
                                step: 0.5,
                              }),
                            )
                          }
                        />
                      </Box>
                      <Box
                        mr={1}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        BVD
                        <RHFTextField
                          sx={{ width: '180px' }}
                          name={`specificationHistories.${index}.rightBVD`}
                          type="number"
                          inputProps={{
                            step: 0.5,
                            min: 0,
                            max: 30,
                          }}
                          onBlur={(e) =>
                            setValue(
                              `specificationHistories.${index}.rightBVD`,
                              normalizeClinicalNumber(e.target.value, {
                                min: 0,
                                max: 30,
                                step: 0.5,
                              }),
                            )
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                  mt={1}
                >
                  <Typography>Left:</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Box
                      mr={1}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <RHFTextField
                        sx={{ width: '150px' }}
                        name={`specificationHistories.${index}.leftSphericalPower`}
                        type="number"
                        inputProps={{
                          step: 0.25,
                          min: -20,
                          max: 20,
                        }}
                        onBlur={(e) =>
                          setValue(
                            `specificationHistories.${index}.leftSphericalPower`,
                            normalizeClinicalNumber(e.target.value, {
                              min: -20,
                              max: 20,
                              step: 0.25,
                            }),
                          )
                        }
                        InputProps={{
                          startAdornment:
                            Number(
                              watch(
                                `specificationHistories.${index}.leftSphericalPower`,
                              ),
                            ) > 0
                              ? '+'
                              : null,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        width: '1px',
                        margin: '0 10px',
                        height: '40px',
                        background: 'black',
                        position: 'relative',
                        top: '10px',
                        transform: 'rotate(14deg)',
                      }}
                    ></Box>
                    <Box
                      mr={1}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <RHFTextField
                        sx={{ width: '180px' }}
                        name={`specificationHistories.${index}.leftCylinder`}
                        type="number"
                        inputProps={{
                          step: 0.25,
                          min: -10,
                          max: 0,
                        }}
                        onBlur={(e) =>
                          setValue(
                            `specificationHistories.${index}.leftCylinder`,
                            normalizeClinicalNumber(e.target.value, {
                              min: -10,
                              max: 0,
                              step: 0.25,
                              forceNegative: true,
                            }),
                          )
                        }
                      />
                    </Box>
                    <Box
                      mr={1}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <RHFTextField
                        sx={{ width: '180px' }}
                        name={`specificationHistories.${index}.leftAxis`}
                        type="number"
                        inputProps={{
                          step: 1,
                          min: 1,
                          max: 180,
                        }}
                        onBlur={(e) =>
                          setValue(
                            `specificationHistories.${index}.leftAxis`,
                            normalizeClinicalNumber(e.target.value, {
                              min: 1,
                              max: 180,
                              integer: true,
                            }),
                          )
                        }
                      />
                      {errorMessages['leftAxis'] && (
                        <Typography color="error" variant="caption">
                          {errorMessages['leftAxis']}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      mr={1}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <RHFTextField
                        sx={{ width: '180px' }}
                        name={`specificationHistories.${index}.leftDistancePD`}
                        type="number"
                        inputProps={{
                          step: 0.5,
                          min: 20,
                          max: 45,
                        }}
                        onBlur={(e) =>
                          setValue(
                            `specificationHistories.${index}.leftDistancePD`,
                            normalizeClinicalNumber(e.target.value, {
                              min: 20,
                              max: 45,
                              step: 0.5,
                            }),
                          )
                        }
                      />
                    </Box>
                    <Box
                      mr={1}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <RHFTextField
                        sx={{ width: '180px' }}
                        name={`specificationHistories.${index}.leftBVD`}
                        type="number"
                        inputProps={{
                          step: 0.5,
                          min: 0,
                          max: 30,
                        }}
                        onBlur={(e) =>
                          setValue(
                            `specificationHistories.${index}.leftBVD`,
                            normalizeClinicalNumber(e.target.value, {
                              min: 0,
                              max: 30,
                              step: 0.5,
                            }),
                          )
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </>
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6">Eye Pressure:</Typography>
          <Box>
            <AddEyePressuresButton
              fields={eyePressuresFields}
              append={appendEyePressure}
            />
          </Box>
        </Box>{' '}
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          {eyePressuresFields.map((pressure, index) => (
            <Box
              key={pressure.id}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              <RHFTextField
                sx={{ mt: 2 }}
                type="number"
                name={`eyePressures.${index}.IOPs`}
                label="IOPs (mmHg) *"
                error={Boolean(errorMessages[iopErrorKey(pressure.id)])}
                inputProps={{
                  step: 0.1,
                  min: IOP_MIN,
                  max: IOP_MAX,
                }}
                onBlur={(e) => {
                  validateIOPsValue(e.target.value, pressure.id);
                }}
              />
              {errorMessages[iopErrorKey(pressure.id)] ? (
                <Typography color="error" variant="caption">
                  {errorMessages[iopErrorKey(pressure.id)]}
                </Typography>
              ) : null}
              <RHFTextField
                sx={{ mt: 2 }}
                name={`eyePressures.${index}.CCT`}
                type="number"
                label="CCT (µm)"
                error={Boolean(errorMessages[cctErrorKey(pressure.id)])}
                inputProps={{
                  step: 1,
                  min: CCT_MIN,
                  max: CCT_MAX,
                }}
                onBlur={(e) => {
                  validateCCTValue(e.target.value, pressure.id);
                }}
              />
              {errorMessages[cctErrorKey(pressure.id)] ? (
                <Typography color="error" variant="caption">
                  {errorMessages[cctErrorKey(pressure.id)]}
                </Typography>
              ) : null}
              <RHFTextField
                sx={{ mt: 2 }}
                name={`eyePressures.${index}.instrument`}
                label="Instrument"
              />
              <IconButton
                onClick={() => {
                  removeEyePressure(index);
                  setErrorMessages((prev) => {
                    const next = { ...prev };
                    delete next[iopErrorKey(pressure.id)];
                    delete next[cctErrorKey(pressure.id)];
                    return next;
                  });
                }}
                sx={{ width: '20%', margin: 'auto' }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          {eyePressuresFields.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <RHFTextField
                sx={{ mt: 2 }}
                value={averages?.IOPs}
                InputProps={{
                  readOnly: true,
                }}
                label="Average IOPs"
                name="avgIOP"
                inputProps={{
                  onChange: (e: any) => {
                    const value = parseFloat(e.target.value).toFixed(2);
                    e.target.value = value;
                  },
                }}
              />
              <RHFTextField
                sx={{ mt: 2 }}
                value={averages?.CCTs}
                InputProps={{
                  readOnly: true,
                }}
                label="Average CCT"
                name="avgCCT"
                inputProps={{
                  onChange: (e: any) => {
                    const value = parseFloat(e.target.value).toFixed(2);
                    e.target.value = value;
                  },
                }}
              />
            </Box>
          )}
        </Box>
        <Typography variant="h6">Auto Refractor:</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          ></Box>
          <Box
            sx={{
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              mt={1}
            >
              <Typography>Right:</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box
                  mr={1}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  Sph
                  <RHFTextField
                    sx={{ width: '150px' }}
                    name="autoRefractor.rightSphericalPower"
                    type="number"
                    inputProps={{
                      step: 0.25,
                      min: -20,
                      max: 20,
                    }}
                    onBlur={(e) =>
                      setValue(
                        'autoRefractor.rightSphericalPower',
                        normalizeClinicalNumber(e.target.value, {
                          min: -20,
                          max: 20,
                          step: 0.25,
                        }),
                      )
                    }
                    InputProps={{
                      startAdornment:
                        Number(watch('autoRefractor.rightSphericalPower')) > 0
                          ? '+'
                          : null,
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    width: '1px',
                    margin: '0 10px',
                    height: '40px',
                    background: 'black',
                    position: 'relative',
                    top: '30px',
                    transform: 'rotate(14deg)',
                  }}
                ></Box>
                <Box
                  mr={1}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  Cyl
                  <RHFTextField
                    sx={{ width: '180px' }}
                    name="autoRefractor.rightCylinder"
                    type="number"
                    inputProps={{
                      step: 0.25,
                      min: -10,
                      max: 0,
                    }}
                    onBlur={(e) =>
                      setValue(
                        'autoRefractor.rightCylinder',
                        normalizeClinicalNumber(e.target.value, {
                          min: -10,
                          max: 0,
                          step: 0.25,
                          forceNegative: true,
                        }),
                      )
                    }
                  />
                </Box>
                <Box
                  mr={1}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  Axis
                  <RHFTextField
                    sx={{ width: '180px' }}
                    name="autoRefractor.rightAxis"
                    type="number"
                    inputProps={{
                      step: 1,
                      min: 1,
                      max: 180,
                    }}
                    onBlur={(e) =>
                      setValue(
                        'autoRefractor.rightAxis',
                        normalizeClinicalNumber(e.target.value, {
                          min: 1,
                          max: 180,
                          integer: true,
                        }),
                      )
                    }
                  />
                  {errorMessages['rightAxis'] && (
                    <Typography color="error" variant="caption">
                      {errorMessages['rightAxis']}
                    </Typography>
                  )}
                </Box>
                <Box
                  mr={1}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  Dist PD
                  <RHFTextField
                    sx={{ width: '180px' }}
                    name="autoRefractor.rightDistancePD"
                    type="number"
                    inputProps={{
                      step: 0.5,
                      min: 20,
                      max: 45,
                    }}
                    onBlur={(e) =>
                      setValue(
                        'autoRefractor.rightDistancePD',
                        normalizeClinicalNumber(e.target.value, {
                          min: 20,
                          max: 45,
                          step: 0.5,
                        }),
                      )
                    }
                  />
                </Box>
                <Box
                  mr={1}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  BVD
                  <RHFTextField
                    sx={{ width: '180px' }}
                    name="autoRefractor.rightBVD"
                    type="number"
                    inputProps={{
                      step: 0.5,
                      min: 0,
                      max: 30,
                    }}
                    onBlur={(e) =>
                      setValue(
                        'autoRefractor.rightBVD',
                        normalizeClinicalNumber(e.target.value, {
                          min: 0,
                          max: 30,
                          step: 0.5,
                        }),
                      )
                    }
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
            mt={1}
          >
            <Typography>Left:</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <Box
                mr={1}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <RHFTextField
                  sx={{ width: '150px' }}
                  name="autoRefractor.leftSphericalPower"
                  type="number"
                  inputProps={{
                    step: 0.25,
                    min: -20,
                    max: 20,
                  }}
                  onBlur={(e) =>
                    setValue(
                      'autoRefractor.leftSphericalPower',
                      normalizeClinicalNumber(e.target.value, {
                        min: -20,
                        max: 20,
                        step: 0.25,
                      }),
                    )
                  }
                  InputProps={{
                    startAdornment:
                      Number(watch('autoRefractor.leftSphericalPower')) > 0
                        ? '+'
                        : null,
                  }}
                />
              </Box>
              <Box
                sx={{
                  width: '1px',
                  margin: '0 10px',
                  height: '40px',
                  background: 'black',
                  position: 'relative',
                  top: '10px',
                  transform: 'rotate(14deg)',
                }}
              ></Box>
              <Box
                mr={1}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <RHFTextField
                  sx={{ width: '180px' }}
                  name="autoRefractor.leftCylinder"
                  type="number"
                  inputProps={{
                    step: 0.25,
                    min: -10,
                    max: 0,
                  }}
                  onBlur={(e) =>
                    setValue(
                      'autoRefractor.leftCylinder',
                      normalizeClinicalNumber(e.target.value, {
                        min: -10,
                        max: 0,
                        step: 0.25,
                        forceNegative: true,
                      }),
                    )
                  }
                />
              </Box>
              <Box
                mr={1}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <RHFTextField
                  sx={{ width: '180px' }}
                  name="autoRefractor.leftAxis"
                  type="number"
                  inputProps={{
                    step: 1,
                    min: 1,
                    max: 180,
                  }}
                  onBlur={(e) =>
                    setValue(
                      'autoRefractor.leftAxis',
                      normalizeClinicalNumber(e.target.value, {
                        min: 1,
                        max: 180,
                        integer: true,
                      }),
                    )
                  }
                />
                {errorMessages['leftAxis'] && (
                  <Typography color="error" variant="caption">
                    {errorMessages['leftAxis']}
                  </Typography>
                )}
              </Box>
              <Box
                mr={1}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <RHFTextField
                  sx={{ width: '180px' }}
                  name="autoRefractor.leftDistancePD"
                  type="number"
                  inputProps={{
                    step: 0.5,
                    min: 20,
                    max: 45,
                  }}
                  onBlur={(e) =>
                    setValue(
                      'autoRefractor.leftDistancePD',
                      normalizeClinicalNumber(e.target.value, {
                        min: 20,
                        max: 45,
                        step: 0.5,
                      }),
                    )
                  }
                />
              </Box>
              <Box
                mr={1}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <RHFTextField
                  sx={{ width: '180px' }}
                  name="autoRefractor.leftBVD"
                  type="number"
                  inputProps={{
                    step: 0.5,
                    min: 0,
                    max: 30,
                  }}
                  onBlur={(e) =>
                    setValue(
                      'autoRefractor.leftBVD',
                      normalizeClinicalNumber(e.target.value, {
                        min: 0,
                        max: 30,
                        step: 0.5,
                      }),
                    )
                  }
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Stack>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" color="inherit" onClick={onCancel}>
          Cancel
        </Button>
        <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
          Add
        </LoadingButton>
      </DialogActions>
      <ConfirmDialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        title="Add a New Tag"
        content={
          <>
            Are you sure want to add <strong> {currentFreeSoloValue} </strong>{' '}
            to tags{' '}
          </>
        }
        action={
          <Button
            variant="contained"
            color="success"
            onClick={() =>
              currentFreeSoloValue &&
              currentFieldName &&
              handleAddTag(currentFreeSoloValue, currentFieldName)
            }
          >
            Add
          </Button>
        }
      />
    </FormProvider>
  );
};

export default AddNewPrescreening;
